import { useState } from 'react'

import { type SplitSupplyPartnerNoticeSkippedPoint } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { CsvDiffTable } from '~/components/common/CsvUpload/CsvDiffTable'
import { Modal } from '~/components/common/Modal'
import { SKIP_REASON_KEYS } from '~/pages/SplitSupplyPlan/models/partnerImport'

const NO_VALUE = '—'

/** XML の取込の結果。 登録した供給地点の数と、 スキップした供給地点 (ダイアログの行と同じものを数える)。 */
export type PartnerImportResult = {
  registeredPointCount: number
  skippedPointCount: number
  /** backend が返した先頭の分 (多い時は件数だけが全部)。 */
  skippedPoints: SplitSupplyPartnerNoticeSkippedPoint[]
}

type Props = {
  result: PartnerImportResult
  onDismiss: () => void
}

/**
 * 他社の通告値を XML で取り込み、 スキップした供給地点が 1 つでもあった時のエラーバナー (AC-S25)。
 * 「{N}件の供給地点の通告値を登録しました。{M}件はスキップしました」 を出し ({N} が 0 なら 1 文目は出さない)、
 * 「詳細を見る」 で開くダイアログにスキップした供給地点特定番号と理由を並べる
 * (`conventions.md`「警告・案内で対象を列挙する時の件数と上限」、 AC-S15 の成功トースト・ダイアログの形)。
 * × で閉じる。
 */
export const PartnerImportResultBanner = ({
  result,
  onDismiss,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const sentences = [
    ...(result.registeredPointCount > 0
      ? [
          t('page:splitSupplyPlan.partner.import.result.registered', {
            count: result.registeredPointCount,
          }),
        ]
      : []),
    t('page:splitSupplyPlan.partner.import.result.skipped', {
      count: result.skippedPointCount,
    }),
  ]
  return (
    <>
      <Alert
        flexShrink={0}
        description={
          <>
            {sentences.map((sentence) => (
              <Text key={sentence}>{sentence}</Text>
            ))}
          </>
        }
        status="error"
      >
        <AlertActions>
          <TertiaryButton
            onClick={() => {
              setDetailsOpen(true)
            }}
            size="sm"
          >
            {t('page:splitSupplyPlan.partner.import.result.showDetails')}
          </TertiaryButton>
          <AlertDismissButton onClick={onDismiss} />
        </AlertActions>
      </Alert>
      <Modal
        footer={
          <TertiaryButton
            onClick={() => {
              setDetailsOpen(false)
            }}
            size="md"
          >
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false)
        }}
        scrollBehavior="inside"
        title={t('page:splitSupplyPlan.partner.import.result.detailsTitle')}
      >
        <Flex flexDirection="column" gap={2}>
          <CsvDiffTable
            columns={[
              {
                key: 'point',
                header: t(
                  'page:splitSupplyPlan.partner.import.column.supplyPoint',
                ),
                mono: true,
                cell: (
                  row: SplitSupplyPartnerNoticeSkippedPoint & {
                    rowIndex: number
                  },
                ) => row.supplyPointNumber,
              },
              {
                key: 'reason',
                header: t(
                  'page:splitSupplyPlan.partner.import.column.skipReason',
                ),
                cell: (row) => {
                  const key = SKIP_REASON_KEYS[row.reason]
                  return key === undefined ? NO_VALUE : t(key)
                },
              },
            ]}
            // 同じ供給地点が 2 つの電文にあって外れた時や、 月間の 2 か月の両方で外れた時は、 ファイル・地点・理由が
            // 同じ行が並ぶ (件数もこの行で数える) ので、 並びの位置で区別する
            getRowKey={(row) => String(row.rowIndex)}
            getRowTone={() => undefined}
            rows={result.skippedPoints.map((point, rowIndex) => ({
              ...point,
              rowIndex,
            }))}
          />
          {result.skippedPoints.length < result.skippedPointCount && (
            <Text textStyle="caption" color="Text.midEmphasis">
              {t('page:splitSupplyPlan.partner.import.skipped.truncated', {
                shown: result.skippedPoints.length,
              })}
            </Text>
          )}
        </Flex>
      </Modal>
    </>
  )
}
